<template>
  <div>
    <HeaderBreadcrumbs
      :items="[{ label: 'User Renewal' }]"
      title="All User Renewal"
    />

    <!-- Table actions: Search and filter -->
    <div class="flex-col w-full overflow-visible bg-white rounded-lg">
      <div class="flex items-center justify-between w-full px-4 py-3 mt-3 overflow-visible rounded-lg">

        <div class="relative w-2/6">
          <SearchOutline className="text-xl absolute top-1/2 left-3 transform -translate-y-1/2" />
          <input
            type="search"
            placeholder="Search Email"
            class="p-2.5 pl-5 w-full text-sm border rounded-md bg-gray-50"
            v-model="searchTerm"
            @keyup.enter="onSearch"
            @input="onSearch"
          />
        </div>

        <div class="relative flex justify-end w-3/4 overflow-visible">
          <!-- Filter button with dropdown -->
          <div class="relative overflow-visible" ref="filterContainer">
            <Button
              @click="showFilterModal = !showFilterModal"
              variant="outlined"
              color="alternative"
              class="relative"
            >
              <template #iconBefore>
                <FilterFilled className="h-3 w-3 text-gray-800" />
              </template>
              Filter
              <template #iconAfter>
                <ChevronDownFilled className="h-3 w-3 text-gray-800" />
              </template>

              <!-- Filter count badge -->
              <span
                v-if="activeFilterCount > 0"
                class="absolute flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full -top-2 -right-2"
              >
                {{ activeFilterCount }}
              </span>
            </Button>

            <!-- Filter Dropdown -->
            <RenewalFilterModal
              :visible="showFilterModal"
              :initial-filters="appliedFilters"
              @apply-filter="handleApplyFilter"
              @reset-filter="handleResetFilter"
              @update:visible="showFilterModal = false"
            />
          </div>
        </div>

      </div>
    </div>

    <Table
      :headers="tableHeaders"
      :data="renewals"
      :rowKey="tableRowKeys"
      :currentPage="currentPage"
      :perPage="perPage"
      :isLoading="isLoading"
      :withScrollX="true"
      :withAction="false"
      className="mb-2"
      @sort="handleTableSort"
    >
      <template #masked_credit_card="{ value }">
        <div>
          {{ hideCode(value, 'masked') }}
        </div>
      </template>
      <template #renewal_status="{ value }">
        <div>
          <Label
            :variant="getStatusVariant(value)"
          >
            {{ capitalizeFirst(value || 'Unknown') }}
          </Label>
        </div>
      </template>
      <template #start_date='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
      <template #expired_date='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
    </Table>

    <!-- Pagination -->
    <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 mb-2 bg-white border-t border-gray-100" v-if="Number(totalRows)">
      <div class="font-semibold">
        <span class="font-normal text-gray-500">Showing </span> {{ currentPage }}-{{ Number(totalRows) >= perPage ? perPage : Number(totalRows) }} <span
          class="font-normal text-gray-500">of</span> {{ parseInt(totalRows) }}
      </div>
      <div class="mb-2">
        <t-pagination :disabled="isLoading" :total-items="Number(totalRows)" :per-page="perPage" v-model="currentPage"
          @change="onClickPage" />
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment';
import { mapState, mapGetters, mapActions } from 'vuex';

import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import SearchOutline from '@/components/icons/SearchOutline';
import FilterFilled from '@/components/icons/FilterFilled';
import ChevronDownFilled from '@/components/icons/ChevronDownFilled';
import Table from '@/components/form/table';
import Label from '@/components/ui/Label';
import Button from '@/components/ui/Button';
import RenewalFilterModal from '@/components/ui/RenewalFilterModal';

export default {
  name: 'UserRenewalNew',
  components: {
    HeaderBreadcrumbs,
    SearchOutline,
    FilterFilled,
    ChevronDownFilled,
    Table,
    Label,
    Button,
    RenewalFilterModal
  },
  data() {
    return {
      searchTerm: null,
      perPage: 100,
      currentPage: 1,

      // Filter modal state
      showFilterModal: false,
      appliedFilters: {
        status: null
      },

      // Table settings
      tableHeaders: [
        { label: 'No', key: 'no' },
        { label: 'Email', key: 'email' },
        { label: 'Subscription Name', key: 'name' },
        { label: 'Orderline', key: 'orderline_id' },
        { label: 'Mask Credit Card', key: 'masked_credit_card' },
        { label: 'Renewal Status', key: 'renewal_status' },
        { label: 'Start Date', key: 'start_date', style: 'width: 120px' },
        { label: 'Expired Date', key: 'expired_date', sortable: true, style: 'width: 120px' },
      ],

      tableRowKeys: [
        'email',
        'name',
        'orderline_id',
        'masked_credit_card',
        'renewal_status',
        'start_date',
        'expired_date'
      ],

      // Sorting
      activeSort: {
        key: null,
        order: 'asc'
      }
    }
  },
  computed: {
    ...mapGetters('renewal', { renewals: 'renewal' }),
    ...mapState({
      isLoading: (state) => state.renewal.isLoading,
      isError: (state) => state.renewal.isError,
      totalRows: (state) => state.renewal.totalRows,
    }),

    activeFilterCount() {
      let count = 0;
      // Count status filter as active if it has a value (including 'all')
      if (this.appliedFilters.status && this.appliedFilters.status !== null) count++;
      return count;
    }
  },
  mounted() {
    this.actionFetchAll();
    // Add click outside listener for filter modal
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    currentPage: function() {
      this.actionFetchAll();
    },
    $route: {
      immediate: true,
      handler(to) {
        document.title =
          to.meta.title ||
          'Customer Active Renewal | Content Management System Ebooks Gramedia.com';
      },
    },
  },
  methods: {
    ...mapActions('renewal', ['fetch', 'search']),
    actionFetchAll() {
      let payload = {
        page: this.currentPage,
        limit: this.perPage,
      };

      // Add search term
      payload.q = this.searchTerm || '';

      // Add filter parameters - use apiStatus if available, otherwise fallback to status or 'all'
      const statusValue = this.appliedFilters.apiStatus !== undefined ? this.appliedFilters.apiStatus : (this.appliedFilters.status || 'all');
      if (statusValue !== null) {
        payload.status = statusValue;
      }

      // Add sorting parameters (only expired_date is sortable)
      if (this.activeSort.key === 'expired_date') {
        payload.expired_date = this.activeSort.order === 'asc' ? 'ASC' : 'DESC';
      } else {
        payload.expired_date = '';
      }

      this.fetch(payload);
    },
    onSearch(event) {
      const value = event.target.value;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.searchTerm = value;
        this.fetch({
          q: value || '',
          page: 1,
          limit: this.perPage,
          ...((() => {
            const statusValue = this.appliedFilters.apiStatus !== undefined ? this.appliedFilters.apiStatus : (this.appliedFilters.status || 'all');
            return statusValue !== null ? { status: statusValue } : {};
          })()),
          expired_date: this.activeSort.key === 'expired_date' ? (this.activeSort.order === 'asc' ? 'ASC' : 'DESC') : '',
        });
      }, 600);
    },

    handleApplyFilter(filterData) {
      this.appliedFilters = { ...filterData };
      this.currentPage = 1; // Reset to first page when applying filters
      this.actionFetchAll();
    },

    handleResetFilter() {
      this.appliedFilters = {
        status: null
      };
      this.currentPage = 1; // Reset to first page when resetting filters
      this.actionFetchAll();
    },

    handleClickOutside(event) {
      // Check if the click is outside the filter container and not on any dropdown elements
      if (this.$refs.filterContainer && !this.$refs.filterContainer.contains(event.target)) {
        // Check if the click is on any element related to the filter modal or its dropdowns
        const isFilterRelated = event.target.closest('[data-filter-modal], .filter-popup, .renewal-filter-dropdown, .absolute[class*="z-10"]');

        if (!isFilterRelated) {
          this.showFilterModal = false;
        }
      }
    },

    onClickPage(page) {
      this.currentPage = page;
      this.actionFetchAll();
    },

    // Utils
    formatDate(dateString) {
      return moment(dateString).format('DD MMM YYYY')
    },
    formatTime(dateString) {
      return moment(dateString).format('HH:mm:ss')
    },

    getStatusVariant(status) {
      switch (status) {
        case 'new': return 'primary';
        case 'cancel': return 'yellow';
        case 'error': return 'red';
        case 'complete': return 'green';
        default: return 'gray';
      }
    },

    handleTableSort(key) {
      // Only allow sorting on expired_date
      if (key !== 'expired_date') return;

      const newActiveSort = {...this.activeSort};
      if (!this.activeSort.key) {
        newActiveSort.key = key;
      } else if (this.activeSort.key === key) {
        newActiveSort.order = newActiveSort.order === 'desc' ? 'asc' : 'desc'
      } else if (this.activeSort.key !== key) {
        newActiveSort.key = key;
        newActiveSort.order = 'asc'
      }
      this.activeSort = newActiveSort;
      this.currentPage = 1; // Reset to first page when sorting
      this.actionFetchAll();
    },

    hideCode(code, type) {
      if (!code) return '';

      const long = code.length;
      let stars = '';
      for (let i = 0; i < long; i++) {
        stars += '*';
      }
      let subs;
      if (type == 'token') {
        subs = code.substring(1, parseInt(long) - 11);
      } else {
        subs = code.substring(1, parseInt(long) - 1);
      }
      return code.replace(subs, stars);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }
}
</script>
